import PropTypes from 'prop-types';
import { Box, Typography, Tooltip, Button } from '@mui/material';
import { Iconify } from 'src/components';

// A category with no leads has nothing to drill into: show 0 and don't open an empty list.
const hasLeads = (t) => (t.leads ?? t.count ?? 0) > 0;

export default function TreatmentBreakdown({ treatments, onTreatmentClick, onSeeAll }) {
    const formatCurrency = (value) => {
        if (value >= 1000000) {
            return `£${(value / 1000000).toFixed(1)}M`;
        }
        if (value >= 1000) {
            return `£${(value / 1000).toFixed(1)}K`;
        }
        return `£${value}`;
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Tooltip title="Breakdown of leads by treatment type with enquiry volume and pipeline value (total quoted value of leads created in this period)." placement="bottom" arrow
                slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help', mb: 2 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                        Treatment Breakdown
                    </Typography>
                    <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                </Box>
            </Tooltip>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '16px',
                }}
            >
                {treatments.map((treatment) => (
                    <Box
                        key={treatment.id}
                        data-testid="treatment-breakdown-card"
                        onClick={hasLeads(treatment) ? () => onTreatmentClick?.(treatment) : undefined}
                        sx={{
                            bgcolor: '#fff',
                            borderRadius: '16px',
                            p: '20px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            borderLeft: `4px solid ${treatment.color || '#6366f1'}`,
                            cursor: hasLeads(treatment) ? 'pointer' : 'default',
                            transition: 'all 0.2s ease',
                            '&:hover': hasLeads(treatment) ? {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                transform: 'translateY(-2px)',
                            } : {},
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                mb: 2,
                                color: '#1e293b',
                            }}
                        >
                            {treatment.name}
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                    sx={{
                                        fontSize: '20px',
                                        fontWeight: 700,
                                        color: treatment.color || '#6366f1',
                                    }}
                                >
                                    {(treatment.leads ?? treatment.count ?? 0).toLocaleString()}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        color: '#64748b',
                                    }}
                                >
                                    Leads
                                </Typography>
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                    sx={{
                                        fontSize: '20px',
                                        fontWeight: 700,
                                        color: '#10b981',
                                    }}
                                >
                                    {formatCurrency(treatment.revenue || 0)}
                                </Typography>
                                <Tooltip title="Total quoted value of leads created in this period" placement="top" arrow>
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            color: '#64748b',
                                            cursor: 'help',
                                        }}
                                    >
                                        Pipeline value
                                    </Typography>
                                </Tooltip>
                            </Box>

                        </Box>
                    </Box>
                ))}
            </Box>

            {treatments.length > 0 && onSeeAll && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={onSeeAll}
                        endIcon={<Iconify icon="eva:arrow-forward-outline" width={16} />}
                        sx={{
                            textTransform: 'none',
                            color: '#6366f1',
                            fontWeight: 600,
                            fontSize: '13px',
                            '&:hover': { bgcolor: 'rgba(99,102,241,0.08)' },
                        }}
                    >
                        See all treatments
                    </Button>
                </Box>
            )}

            {treatments.length === 0 && (
                <Box
                    sx={{
                        textAlign: 'center',
                        py: 6,
                        bgcolor: '#fff',
                        borderRadius: '16px',
                    }}
                >
                    <Typography sx={{ color: '#64748b' }}>
                        No treatment data available
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

TreatmentBreakdown.propTypes = {
    treatments: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            leads: PropTypes.number,
            count: PropTypes.number,
            revenue: PropTypes.number,
            color: PropTypes.string,
        })
    ).isRequired,
    onTreatmentClick: PropTypes.func,
    onSeeAll: PropTypes.func,
};

TreatmentBreakdown.defaultProps = {
    onTreatmentClick: null,
    onSeeAll: null,
};
